<template>
	<div
		ref="listEl"
		class="tag-list"
	>
		<div
			class="tag-list__spacer"
			:style="{ height: `${totalSize}px` }"
		>
			<div
				v-for="row in visibleRows"
				:key="row.tag.Id"
				:ref="measureRow"
				:data-index="row.index"
				class="tag-list__row"
				:style="{ transform: `translateY(${row.start}px)` }"
			>
				<app-checkbox
					:style="{ height: '16px', marginRight: '8px', marginTop: '1px' }"
					:value="selectedIds.has(row.tag.Id)"
					text=""
					@on-input="emit('toggle', row.tag)"
				/>
				<div
					class="tag-list__text"
					@click="emit('toggle', row.tag)"
					v-html="formatTagLabel(row.tag)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useVirtualizer } from '@tanstack/vue-virtual';
import AppCheckbox from '@/components/ui-kit/checkbox/AppCheckbox.vue';
import type { TagWithFrequency } from '@/models/interfaces/tags/tag-models';
import { escapeHtml, highlightSearchText } from '@/utils/textHelpers';

const ROW_ESTIMATE = 26;

type AppTagSelectorListProps = {
	tags: TagWithFrequency[];
	selectedIds: Set<number>;
	searchText?: string;
};

type AppTagSelectorListEmits = {
	toggle: [tag: TagWithFrequency];
};

const props = defineProps<AppTagSelectorListProps>();
const emit = defineEmits<AppTagSelectorListEmits>();

const listEl = ref<HTMLElement | null>(null);

const rowVirtualizer = useVirtualizer(computed(() => ({
	count: props.tags.length,
	getScrollElement: () => listEl.value,
	estimateSize: () => ROW_ESTIMATE,
	overscan: 8,
	getItemKey: (index: number) => props.tags[index]?.Id ?? index,
})));

const totalSize = computed<number>(() => rowVirtualizer.value.getTotalSize());

// При резкой смене данных (поиск, фоновое обновление списка) виртуализатор может
// на один тик отдать индексы от прежнего набора — такие строки отбрасываем,
// чтобы шаблон не читал tags[index] как undefined.
const visibleRows = computed(() => {
	return rowVirtualizer.value
		.getVirtualItems()
		.map((item) => ({ index: item.index, start: item.start, tag: props.tags[item.index] }))
		.filter((row) => !!row.tag);
});

const measureRow = (el: unknown) => {
	if (!el) {
		return;
	}
	rowVirtualizer.value.measureElement(el as HTMLElement);
};

const formatTagLabel = (tag: TagWithFrequency): string => {
	const name = highlightSearchText(props.searchText ?? '', escapeHtml((tag.Name ?? '').toLowerCase()));
	return `${name} (${tag.Frequency})`;
};
</script>

<style lang="scss" scoped>
@import '@/styles/color.scss';

.tag-list {
	flex: 1;
	min-height: 0;
	overflow-y: auto;

	&__spacer {
		position: relative;
		width: 100%;
	}

	&__row {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		display: flex;
		align-items: flex-start;
		padding: 0 0 8px;
		cursor: pointer;
		font-size: 14px;
		line-height: 18px;
		color: #333;
		transition: color 0.1s;

		&:hover .tag-list__text {
			text-decoration: underline;
			color: #396742;
		}
	}

	&__text {
		font-size: 14px;
		line-height: 18px;
		word-break: break-word;
		white-space: normal;
	}
}
</style>
